import { useLayoutEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { TicketDetail, TicketParentCandidates, TicketSpalte, TicketSubtaskCreated, TicketSubtaskRow }
  from "@kundeninfo/api-client";
import { Modal, useBestaetigung, useSofortPopup } from "@kundeninfo/ui";
import { Abschnitt, Auswahlmenue, Chip, Eingabe, Hinweis, Objektsymbol, Tabelle } from "@kundeninfo/ui/me-design";
import { KiKnopf, Suchfeld, useEingabetaste } from "../../components/meHuellen.js";
import { keepInView } from "../../scroll.js";
import { personOption } from "./Avatar.js";
import { OwnerBadge } from "./OwnerBadge.js";
import { HIERARCHY_TEXT } from "./hierarchyTexts.js";
import { dueMark, isOpen, numberPopup } from "./hierarchy.js";
import { ticketReceipt } from "./receipts.js";
import { tagBeschriftung } from "./ticketFormat.js";
import { SchreibHinweis, useSchreibweg } from "./useSchreibweg.js";
import type { TicketsClient } from "./ticketsClient.js";

/**
 * DER ABSCHNITT „TEILAUFGABEN" (Spec Teilaufgaben R21, R22): im Slot `abschnitt` des Ticketdialogs, Titel, Zähler „1/4",
 * Objektsymbol im Slot `icon`, „Im Board zeigen" in den Aktionen (R26, ab Aufgabe 19). Die Tabelle zeigt je direktes Kind
 * Kästchen (angehakt ⇔ Statusart DONE, R10; natives Kästchen, die Optik gibt tokens.css von me-design, Entscheid 82),
 * Anzeige-Nummer, Titel mit eigenem Fortschritt, Person (Eigentümer), Status, Wiedervorlage und Fällig bis. Die Zeile
 * öffnet das Kind seitlich (R23): mit der Maus überall außer am Kästchen, mit der Tastatur über den Titel — ein leiser
 * Knopf wie in der Ticketliste (Tabulator-Halt, Name = Titel, Enter/Leertaste). Darunter die Schnellanlage mit Quittung
 * und echtem Rückgängig (R14) und „Vorhandenes Ticket zuordnen …" (E441). Jeder Schreibweg über `useSchreibweg`; vor
 * jeder Übernahme merkt der Abschnitt die Lage der Schnellanlage und gleicht nach dem Zeichnen aus (Hausregel „Scroll
 * bei Neuzeichnen“, `keepInView`) — eine neue oder entfallene Zeile darüber verschiebt nichts.
 *
 * E437: Die Schnellanlage unter einem Ticket mit Eigentümer „System" macht mich zum Eigentümer und hebt dessen Version —
 * die Antwort trägt das Elternticket, der Dialog übernimmt es (`onNeu`), sonst endete das nächste Speichern in 409.
 * Rückgängig auf ein schon zurückgenommenes Kind antwortet 404 (der Klick kam zu spät, kein Fehler der Nutzerin): Der
 * Abschnitt lädt dann nur das Elternticket nach. Kästchen und Zuordnen antworten nicht mit dem Elternticket: Es wird nach
 * der Handlung geholt; scheitert NUR das, steht die Handlung — der Hinweis sagt genau das, ohne Quittung daneben. Alle
 * Quittungen trägt der Ticketdialog um den Abschnitt (nie das Zuordnen-Modal, das gleich zugeht).
 */

const STATUS_ART: Record<string, "ok" | "warnung" | "laeuft"> = { OPEN: "laeuft", WAITING: "warnung", DONE: "ok" };

function ownerName(o: TicketSubtaskRow["owner"]): string | null {
  return o.kind === "USER" ? o.user?.name ?? null : o.kind === "TEAM" ? o.team?.shortCode ?? null : null;
}

function Zeile(p: { z: TicketSubtaskRow; spalte: string; heute: string; ichId: string; gesperrt: boolean;
                   onKaestchen: (z: TicketSubtaskRow) => void; onOeffnen: (id: string) => void }): ReactElement {
  const z = p.z;
  const nummer = useSofortPopup<HTMLSpanElement>(numberPopup({ ...z, parentTicketNumber: null }));
  const titel = useSofortPopup<HTMLSpanElement>(z.title);
  const person = useSofortPopup<HTMLSpanElement>(HIERARCHY_TEXT.ownerOf(ownerName(z.owner) ?? HIERARCHY_TEXT.system));
  const erledigt = !isOpen(z.stateKind);
  const faellig = dueMark(z.dueDate, p.heute, erledigt);
  return (
    <tr className="ts__zeile" onClick={(e) => { if ((e.target as Element).closest("input, me-knopf") === null) p.onOeffnen(z.id); }}>
      <td className="ts__kaestchen">
        <input type="checkbox" checked={erledigt} disabled={p.gesperrt}
          aria-label={`${z.displayNumber} ${z.title}`} onChange={() => p.onKaestchen(z)} />
      </td>
      <td><span ref={nummer} className="ts__nummer">{z.displayNumber}</span></td>
      {/* Die Zelle trägt nichts zur Mindestbreite der Tabelle bei (`ts__titelzelle`); die Anordnung liegt in einem Block
          darin — ein `td` mit `display: flex` wäre kein Tabellenfeld mehr und behielte die volle Titelbreite. */}
      <td className="ts__titelzelle">
        <div className="ts__titelfeld">
          <KiKnopf art="leise" className="ts__titelknopf" onClick={() => p.onOeffnen(z.id)}>
            <span ref={titel} className="ts__titel">{z.title}</span>
          </KiKnopf>
          {z.subtaskTotal > 0 && <Chip>{HIERARCHY_TEXT.progress(z.subtaskDone, z.subtaskTotal)}</Chip>}
        </div>
      </td>
      <td><span ref={person} className="ts__person"><OwnerBadge owner={z.owner} ichId={p.ichId} mitName ohneTipp /></span></td>
      <td><Chip statusArt={z.status === "ARCHIVED" ? "aus" : STATUS_ART[z.stateKind] ?? "laeuft"}>{p.spalte}</Chip></td>
      <td>{z.resubmitDate === null ? HIERARCHY_TEXT.none
        : `${tagBeschriftung(z.resubmitDate)}${z.resubmitTime === null ? "" : ` ${z.resubmitTime}`}`}</td>
      <td>{faellig === null ? HIERARCHY_TEXT.none
        : faellig.art === null ? <Chip>{faellig.text}</Chip> : <Chip statusArt={faellig.art}>{faellig.text}</Chip>}</td>
    </tr>
  );
}

export type SubtaskSectionProps = {
  d: TicketDetail; client: TicketsClient; spalten: readonly TicketSpalte[];
  leute: readonly { id: string; name: string; email: string | null }[]; ichId: string;
  entwurf: string; onEntwurf: (w: string) => void; onNeu: (d: TicketDetail) => void; nachladen: () => Promise<TicketDetail>;
  onOpenChild: (id: string) => void; onShowInBoard?: (id: string) => void;
};

export function SubtaskSection(p: SubtaskSectionProps): ReactElement {
  const d = p.d;
  const [offen, setOffen] = useState(d.subtasks.length > 0);
  const [person, setPerson] = useState("");
  const [faellig, setFaellig] = useState("");
  const [zuordnen, setZuordnen] = useState(false);
  const titelHost = useRef<HTMLElement | null>(null);
  const wurzel = useRef<HTMLElement | null>(null);
  const schnell = useRef<HTMLDivElement | null>(null);
  const ausgleich = useRef<(() => void) | null>(null);
  // Hausregel „Scroll bei Neuzeichnen“: Lage der Schnellanlage unmittelbar vor der Übernahme, Ausgleich nach dem Zeichnen.
  useLayoutEffect(() => { ausgleich.current?.(); ausgleich.current = null; });
  const weg = useSchreibweg<TicketDetail>((neu) => {
    if (schnell.current !== null) ausgleich.current = keepInView(schnell.current);
    p.onNeu(neu);
  }, { nachladen: p.nachladen, felder: ["title", "assigneeUserId", "dueDate"] });
  const { bestaetigen } = useBestaetigung();
  const erledigt = d.subtasks.filter((z) => !isOpen(z.stateKind)).length;
  const spaltenName = (id: string) => p.spalten.find((s) => s.id === id)?.name ?? "";

  /**
   * Schreibhandlungen, deren Antwort nicht das Elternticket ist (Kästchen, Gegenbuchung, Zuordnen): erst schreiben, dann
   * das Elternticket holen. Befunde der Handlung wie jeder Schreibweg; scheitert NUR das Nachladen, bleibt der bekannte
   * Stand stehen, der Hinweis sagt es, und es gibt `undefined` — also keine Quittung daneben.
   */
  async function schreibenUndNachladen<K>(arbeit: () => Promise<K>, sonst: string): Promise<K | undefined> {
    const r: { k?: K; neu?: TicketDetail | null } = {};
    const ok = await weg.schreiben(async () => {
      r.k = await arbeit();
      r.neu = await p.nachladen().catch(() => null);
      return r.neu ?? d;
    }, sonst);
    if (!ok || r.k === undefined) return undefined;
    if (r.neu === null) {
      weg.melden({ art: "warnung", text: HIERARCHY_TEXT.failReload });
      return undefined;
    }
    return r.k;
  }

  /** Rückgängig der Schnellanlage. Ruling 4: Ein schon zurückgenommenes Kind antwortet 404 — dann gilt der Serverstand des
   *  Elterntickets. R14: Ist am Kind inzwischen etwas geschehen, antwortet der Server 409 `TICKET_NOT_WITHDRAWABLE`; das
   *  ist kein Versionskonflikt — der Abschnitt lädt nach und zeigt den Text des Servers (Abschluss-Review M1). */
  async function zuruecknehmen(id: string): Promise<void> {
    const nicht: { text?: string } = {};
    const ok = await weg.schreiben(async () => {
      try {
        return await p.client.withdrawSubtask(id);
      } catch (f) {
        if (f instanceof ApiError && f.status === 404) return await p.nachladen();
        if (f instanceof ApiError && f.code === "TICKET_NOT_WITHDRAWABLE") { nicht.text = f.message; return await p.nachladen(); }
        throw f;
      }
    }, HIERARCHY_TEXT.failUndo);
    if (ok && nicht.text !== undefined) weg.melden({ art: "warnung", text: nicht.text });
  }

  async function anlegen(): Promise<void> {
    const titel = p.entwurf.trim();
    if (titel === "" || weg.laeuft) return;
    const antwort: { r?: TicketSubtaskCreated } = {};
    const ok = await weg.schreiben(async () => {
      antwort.r = await p.client.createSubtask(d.id, { title: titel, assigneeUserId: person === "" ? null : person,
                                                       dueDate: faellig === "" ? null : faellig });
      return antwort.r.ticket;
    }, HIERARCHY_TEXT.failCreate);
    const r = antwort.r;
    if (!ok || r === undefined) return;
    const kind = r.ticket.subtasks.find((z) => z.id === r.createdTicketId);
    const uebernommen = d.owner.kind === "SYSTEM" && r.ticket.owner.kind === "USER" ? d.ticketNumber : null;
    p.onEntwurf("");
    setFaellig("");
    setOffen(true);
    ticketReceipt({
      text: HIERARCHY_TEXT.receiptCreated(kind?.ticketNumber ?? "", p.leute.find((u) => u.id === person)?.name ?? null, uebernommen),
      an: wurzel.current,
      rueckgaengig: () => { void zuruecknehmen(r.createdTicketId); },
    });
    titelHost.current?.focus();
  }
  useEingabetaste(titelHost, () => { void anlegen(); });

  async function kaestchen(z: TicketSubtaskRow): Promise<void> {
    const fertig = isOpen(z.stateKind);
    const art = fertig ? HIERARCHY_TEXT.confirmDone : HIERARCHY_TEXT.confirmReopen;
    if (!(await bestaetigen({ titel: art.title, bestaetigenText: art.button,
      text: HIERARCHY_TEXT.confirmDoneText(z.title, ownerName(z.owner), fertig ? z.subtaskTotal - z.subtaskDone : 0) }))) return;
    const k = await schreibenUndNachladen(() => p.client.setTicketDone(z.id, fertig, z.version), HIERARCHY_TEXT.failChange);
    if (k === undefined) return;
    ticketReceipt({ text: fertig ? HIERARCHY_TEXT.receiptDone(k.displayNumber) : HIERARCHY_TEXT.receiptReopened(k.displayNumber),
      an: wurzel.current,
      rueckgaengig: () => {
        void schreibenUndNachladen(() => p.client.setTicketDone(z.id, !fertig, k.version), HIERARCHY_TEXT.failUndo);
      } });
  }

  /** E441, M2/M3: Das Modal hat zugeordnet und geht zu; der Abschnitt lädt nach und quittiert im Ticketdialog. */
  async function zugeordnet(t: Kandidat): Promise<void> {
    setZuordnen(false);
    if ((await schreibenUndNachladen(async () => t, HIERARCHY_TEXT.failAttach)) === undefined) return;
    ticketReceipt({ text: HIERARCHY_TEXT.receiptAttached(t.ticketNumber, d.ticketNumber), an: wurzel.current });
  }

  return (
    <Abschnitt ref={wurzel} className="ts" titel={HIERARCHY_TEXT.section} zaehler={HIERARCHY_TEXT.progress(erledigt, d.subtasks.length)}
      offen={offen} onUmgeschaltet={(e) => setOffen(e.offen)} icon={<Objektsymbol art="teilaufgabe" groesse={16} />}
      {...(p.onShowInBoard === undefined ? {} : { aktionen: (
        <KiKnopf art="leise" kompakt onClick={() => p.onShowInBoard!(d.id)}>{HIERARCHY_TEXT.showInBoard}</KiKnopf>) })}>
      {d.subtasks.length === 0 ? <p className="ts__leer">{HIERARCHY_TEXT.empty}</p> : (
        <Tabelle stapeln dicht>
          <table>
            <thead><tr>
              <th scope="col"><span className="bt__unsichtbar">{HIERARCHY_TEXT.confirmDone.button}</span></th>
              <th scope="col">{HIERARCHY_TEXT.columnNumber}</th><th scope="col">{HIERARCHY_TEXT.columnTitle}</th>
              <th scope="col">{HIERARCHY_TEXT.person}</th>
              <th scope="col">{HIERARCHY_TEXT.status}</th><th scope="col">{HIERARCHY_TEXT.resubmit}</th>
              <th scope="col">{HIERARCHY_TEXT.dueDate}</th>
            </tr></thead>
            <tbody>
              {d.subtasks.map((z) => (
                <Zeile key={z.id} z={z} spalte={z.status === "ARCHIVED" ? HIERARCHY_TEXT.stateArchived : spaltenName(z.columnId)} heute={d.heute}
                  ichId={p.ichId} gesperrt={weg.laeuft} onKaestchen={(x) => { void kaestchen(x); }} onOeffnen={p.onOpenChild} />
              ))}
            </tbody>
          </table>
        </Tabelle>
      )}
      {d.owner.kind === "SYSTEM" && <Hinweis art="info">{HIERARCHY_TEXT.systemHint}</Hinweis>}
      <div ref={schnell} className="ts__schnell">
        <Eingabe ref={titelHost} aria-label={HIERARCHY_TEXT.quickTitle} beschriftung={HIERARCHY_TEXT.quickTitle} wert={p.entwurf}
          {...(weg.feldFehler.title === undefined ? {} : { fehler: weg.feldFehler.title })}
          onGeaendert={(e) => { weg.feldRaeumen("title"); p.onEntwurf(e.wert); }} />
        <Auswahlmenue kompakt breite="11em" beschriftung={HIERARCHY_TEXT.quickPerson} wert={person} gesperrt={weg.laeuft}
          {...(weg.feldFehler.assigneeUserId === undefined ? {} : { fehler: weg.feldFehler.assigneeUserId })}
          onGeaendert={(e) => { weg.feldRaeumen("assigneeUserId"); setPerson(e.wert); }}>
          <option value="" data-art="niemand">{HIERARCHY_TEXT.quickPersonNone}</option>
          {p.leute.map((u) => <option key={u.id} value={u.id} {...personOption(u)}>{u.name}</option>)}
        </Auswahlmenue>
        <Eingabe art="datum" aria-label={HIERARCHY_TEXT.quickDueDate} beschriftung={HIERARCHY_TEXT.dueDate} wert={faellig}
          {...(weg.feldFehler.dueDate === undefined ? {} : { fehler: weg.feldFehler.dueDate })}
          onGeaendert={(e) => { weg.feldRaeumen("dueDate"); setFaellig(e.wert); }} />
        <KiKnopf art="sekundaer" deaktiviert={p.entwurf.trim() === "" || weg.laeuft} onClick={() => { void anlegen(); }}>
          {HIERARCHY_TEXT.create}
        </KiKnopf>
      </div>
      <KiKnopf art="leise" className="ts__zuordnen" onClick={() => setZuordnen(true)}>{HIERARCHY_TEXT.attachExisting}</KiKnopf>
      <SchreibHinweis meldung={weg.meldung} />
      {zuordnen && <Zuordnen d={d} client={p.client} onAbbrechen={() => setZuordnen(false)}
        onZugeordnet={(t) => { void zugeordnet(t); }} />}
    </Abschnitt>
  );
}

type Kandidat = TicketParentCandidates["items"][number];

/** E441: Suche seitlich (Hausregel „Sub-Inhalte seitlich"), Wahl nach gestapelter Bestätigung; ein Befund des Zuordnens
 *  steht im Modal, das dann offen bleibt. */
function Zuordnen(p: { d: TicketDetail; client: TicketsClient; onZugeordnet: (t: Kandidat) => void; onAbbrechen: () => void }):
    ReactElement {
  const [q, setQ] = useState("");
  const [treffer, setTreffer] = useState<Kandidat[]>([]);
  const [meldung, setMeldung] = useState<string | null>(null);
  const { bestaetigen } = useBestaetigung();
  const lauf = useRef(0);
  async function suchen(text: string): Promise<void> {
    setQ(text);
    const nr = ++lauf.current;
    try {
      const r = await p.client.searchParentCandidates(p.d.id, text);
      if (nr === lauf.current) setTreffer(r.items);
    } catch { if (nr === lauf.current) setTreffer([]); }
  }
  async function waehlen(t: Kandidat): Promise<void> {
    if (!(await bestaetigen({ titel: HIERARCHY_TEXT.attachConfirmTitle(t.ticketNumber, p.d.ticketNumber),
      text: HIERARCHY_TEXT.attachConfirmText(t.parentTicketNumber), bestaetigenText: HIERARCHY_TEXT.attachConfirm }))) return;
    try {
      await p.client.updateTicket(t.id, t.version, { parentTicketId: p.d.id });
    } catch (f) {
      setMeldung(f instanceof ApiError ? (f.fields?.[0]?.message ?? f.message) : HIERARCHY_TEXT.failAttach);
      return;
    }
    p.onZugeordnet(t);
  }
  return (
    <Modal titel={HIERARCHY_TEXT.attachTitle} onAbbrechen={p.onAbbrechen}>
      <Suchfeld name={HIERARCHY_TEXT.attachSearch} beschriftung={HIERARCHY_TEXT.attachSearch} wert={q}
        onText={(t) => { void suchen(t); }} />
      {meldung !== null && <Hinweis art="fehler">{meldung}</Hinweis>}
      {q !== "" && treffer.length === 0 ? <p className="ts__leer">{HIERARCHY_TEXT.attachNone}</p> : (
        <ul className="ts__treffer">
          {treffer.map((t) => (
            <li key={t.id}><KiKnopf art="leise" onClick={() => { void waehlen(t); }}>{`${t.ticketNumber} ${t.title}`}</KiKnopf></li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
